import React, { useState, useEffect } from 'react';
import axiosInstance from "../../interceptor/axiosInstance";
import NotificationService from "../Utils/NotificationService"; 
import "./Org.css";

const EmbedCodeComponent = () => {
  const [orgName, setOrgName] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [isEmailValidated, setIsEmailValidated] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState(null);
  const [email, setEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [tempWebsiteUrl, setTempWebsiteUrl] = useState('');
  const [domainAuthorisation, setDomainAuthorisation] = useState(true);

  useEffect(() => {
    const fetchOrgDetails = async () => {
      try {
        const response = await axiosInstance.get(`/org/org-details`);
        const data = response.data[0];  
        setOrgName(data.organisation_name);  
        setOrgCode(data.organisation_code);  
        setIsEmailValidated(data.is_email_validate);  
        setEmail(data.email);
        setWebsiteUrl(data.website_url);
        setDomainAuthorisation(data.domain_authorisation === 1);
      } catch (error) {
        console.error('Error fetching organization details:', error);
      }
    };

    fetchOrgDetails();
  }, []);  

  const scriptCode = `
<div id="chat-bot-container"></div>
<script src="https://ezzdesk.com/shared_prompt.js?code=${orgCode}"></script>
  `.trim();

  const handleCopy = () => {
    navigator.clipboard.writeText(scriptCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000); 
    }).catch((err) => {
      NotificationService.error('Failed to copy text: ' + err);
    });
  };

  const handleResendVerification = async () => {
    if (isLoading) return;  

    setIsLoading(true);
    setError(null); 

    try {
      const response = await axiosInstance.post('/org/resend-verification-link');
      if (response.data.success) {
        NotificationService.success('Verification link sent! Please check your email.');
      } else {
        NotificationService.error('Failed to resend the verification link.');
      }
    } catch (error) {
      console.error('Error resending verification link:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateWebsiteUrl = async () => {
    if (!tempWebsiteUrl) {
      NotificationService.error("Website URL cannot be empty");
      return;
    }
    const urlRegex = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
    if (!urlRegex.test(tempWebsiteUrl)) {
      NotificationService.error("Please enter a valid Website URL");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.put('/org/update-website-url', { newUrl: tempWebsiteUrl });
      if (response.data && response.data.success) {
        setWebsiteUrl(tempWebsiteUrl);
        setIsEditingUrl(false);
        NotificationService.success("Website URL updated successfully!");
      } else {
        NotificationService.error("Failed to update Website URL.");
      }
    } catch (error) {
      console.error('Error updating website url:', error);
      NotificationService.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDomainAuth = async (e) => {
    const isEnabled = e.target.checked;
    setDomainAuthorisation(isEnabled);
    try {
      const response = await axiosInstance.put('/org/update-domain-auth', { isEnabled });
      if (response.data && response.data.success) {
        NotificationService.success(`Domain Authorisation ${isEnabled ? 'Enabled' : 'Disabled'} successfully!`);
      } else {
        setDomainAuthorisation(!isEnabled); 
        NotificationService.error("Failed to update Domain Authorisation.");
      }
    } catch (error) {
      console.error('Error updating domain authorisation:', error);
      setDomainAuthorisation(!isEnabled); 
      NotificationService.error('Something went wrong. Please try again.');
    }
  };

  const platforms = [
    { name: "React", link: "/react-guide" },
    { name: "HTML", link: "/html-guide" },
    { name: "Godaddy", link: "/godaddy-guide" },
    { name: "Hostinger", link: "/hostinger-guide" },
    { name: "Joomla", link: "/joomla-guide" },
    { name: "Laravel", link: "/laravel-guide" },
    { name: "Shopify", link: "/shopify-guide" },
    { name: "Wix", link: "/wix-guide" },
    { name: "WordPress", link: "/wordpress-guide" },
    { name: "WordPress (Elementor)", link: "/wordpress-elementor-guide" },
  ];

  return (
    <div className="settings-card">
      <h5 className="font-outfit" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '6px' }}>
        Organization Name
      </h5>
      <p style={{ color: 'var(--text-muted)', fontSize: '15px', marginBottom: '24px' }}>
        {orgName || 'Loading...'}
      </p>

      <h5 className="font-outfit" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '6px' }}>
        Website URL
      </h5>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', height: '38px' }}>
        {!isEditingUrl ? (
          <>
            <a 
              href={websiteUrl ? (websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`) : '#'}
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ marginRight: '16px', fontSize: '15px', fontWeight: 500, color: 'var(--primary)', textDecoration: 'none' }}
            >
              {websiteUrl ? websiteUrl : 'Loading...'}
            </a>
            <button
              onClick={() => {
                setTempWebsiteUrl(websiteUrl || '');
                setIsEditingUrl(true);
              }}
              className="btn-color"
              style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              Edit
            </button>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input 
              type="text" 
              value={tempWebsiteUrl} 
              onChange={(e) => setTempWebsiteUrl(e.target.value)} 
              className="input-field"
              style={{ width: '260px', padding: '6px 12px' }}
            />
            <button
              onClick={handleUpdateWebsiteUrl}
              className="btn-color"
              disabled={isLoading}
              style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              Save
            </button>
            <button
              onClick={() => setIsEditingUrl(false)}
              className="btn-color"
              style={{ padding: '6px 14px', fontSize: '13px', background: '#e2e8f0', color: '#475569 !important', boxShadow: 'none' }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <h5 className="font-outfit" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '6px' }}>
        Domain Authorisation
      </h5>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
        <input 
          type="checkbox" 
          id="domainAuth"
          className="form-check-input"
          checked={domainAuthorisation}
          onChange={handleUpdateDomainAuth}
          style={{ width: '18px', height: '18px', cursor: 'pointer', margin: 0 }}
        />
        <label htmlFor="domainAuth" style={{ fontSize: '14px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
          Require authorized domain validation for chat scripts
        </label>
      </div>

      {/* Email Verification Section */}
      {isEmailValidated === 0 && (
        <div style={{ background: 'rgba(187,4,3,0.05)', border: '1px solid rgba(187,4,3,0.15)', padding: '16px 20px', borderRadius: '12px', marginBottom: '28px' }}>
          <h5 className="font-outfit" style={{ fontSize: '16px', fontWeight: 700, color: 'var(--primary)', marginBottom: '6px' }}>
            Verify Email Address
          </h5>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
              {email || 'Loading...'}
            </span>
            <button
              onClick={handleResendVerification}
              className='btn-color'
              style={{ padding: '6px 14px', fontSize: '13px' }}
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Resend Verification Link'}
            </button>
          </div>
          <span style={{ color: 'var(--primary)', fontSize: '13px', fontWeight: 500, marginTop: '8px', display: 'block' }}>
            Please verify your email to get the embedded code working.
          </span>
        </div>
      )}

      {/* Embed Code Section */}
      <h5 className="font-outfit" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '6px' }}>
        Embed Script Code
      </h5>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
        Copy the script below and paste it into your website's HTML source file before the closing &lt;/body&gt; tag.
      </p>

      <div className="code-box" style={{ marginBottom: '32px' }}>
        <button
          onClick={handleCopy}
          className='btn-color'
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            padding: '6px 14px',
            fontSize: '12px',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <code>{scriptCode}</code>
      </div>

      <h5 className="font-outfit" style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', marginBottom: '6px' }}>
        Installation Guides
      </h5>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '16px' }}>
        Choose your platform to get step-by-step instructions:
      </p>

      <div className="guide-list">
        {platforms.map((platform) => (
          <a
            key={platform.name}
            href={platform.link}
            target="_blank"
            rel="noopener noreferrer"
            className="guide-link"
          >
            {platform.name}
          </a>
        ))}
      </div>
    </div>
  );
};

export default EmbedCodeComponent;